import { Language } from "@/i18n/Languages";
import { ComBoxClientConfigInfo } from "./ComBoxClientConfig";
import { createDefaultProjectSettingsV2, ErpIntegration, ProjectManagement } from "./Client";
import { getClientInfo, getProjectManagement } from "../repository/ClientRepository";
import { DocumentsAsSeparateReport, ReportsList, SendReportsOption } from "./Report";
import { ProjectTypeConfig } from "./ProjectDetails";

export enum ProjectDataExchangeOptions {
  NO = "NO",
  CSV = "CSV",
  JSON = "JSON"
}

/**
 * @deprecated
 * The original four table sorting options, superseded by `TableSortingOptionsV2`. Kept because IRES
 * Project deserializes `tableSorting` into a non-nullable enum of exactly these four values and
 * throws on anything else, which takes down the whole settings document rather than just the field.
 * Never extend this enum: a value outside it must not reach `tableSorting`.
 */
export enum TableSortingOptions {
  PHASE_TRADE = "PHASE_TRADE",
  TRADE_PHASE = "TRADE_PHASE",
  APARTMENT_PHASE = "APARTMENT_PHASE",
  APARTMENT_TRADE = "APARTMENT_TRADE"
}

/**
 * How the trades table is grouped and sorted. The value pairs a grouping level (consolidated,
 * per apartment, per room) with the secondary sort (phase first or trade first).
 *
 * Declared in picker order. Stored in `tableSortingV2`; the deprecated `tableSorting` carries the
 * closest legacy equivalent for IRES Project builds that only know the four original options.
 */
export enum TableSortingOptionsV2 {
  PHASE_TRADE = "PHASE_TRADE",
  TRADE_PHASE = "TRADE_PHASE",
  APARTMENT_PHASE = "APARTMENT_PHASE",
  APARTMENT_TRADE = "APARTMENT_TRADE",
  ROOM_PHASE = "ROOM_PHASE",
  ROOM_TRADE = "ROOM_TRADE"
}

/**
 * Interface defining number of months.
 */
export enum NumberOfMonths {
  NEVER = -1,
  THREE = 3,
  SIX = 6,
  NINE = 9,
  TWELVE = 12,
  TWENTY_FOUR = 24,
}

/**
 * Interface defining number of weeks.
 */
export enum NumberOfWeeks {
  NEVER = -1,
  ONE = 1,
  TWO = 2,
  FOUR = 4,
  EIGHT = 8,
  TWELVE = 12
}

/**
 * Interface defining number of days.
 */
export enum NumberOfDays {
  NEVER = -1,
  ONE = 1,
  TWO = 2,
  THREE = 3,
  FOUR = 4,
}

/**
 * Interface defining report image quality.
 */
export enum ReportImageQuality {
  LOW = "LOW",
  NORMAL = "NORMAL",
  HIGH = "HIGH",
}

/**
 * Order in which the sketch, images and text (description / checklist) blocks are drawn within a
 * report section (rooms / apartments, services checklist). The value names spell out the order.
 * Controls the report layout only; the first entry, `SKETCH_IMAGES_TEXT`, is the default and
 * matches the layout used before this option existed. Declaration order is the picker order.
 */
export enum ReportContentOrder {
  SKETCH_IMAGES_TEXT = "SKETCH_IMAGES_TEXT",
  SKETCH_TEXT_IMAGES = "SKETCH_TEXT_IMAGES",
  TEXT_SKETCH_IMAGES = "TEXT_SKETCH_IMAGES",
}

export enum ImageReportAfterDamageAssessment {
  NO = "NO",
  SELECTION_AS_EXTERNAL_REPORT = "SELECTION_AS_EXTERNAL_REPORT",
  SELECTION_AS_INTERNAL_REPORT = "SELECTION_AS_INTERNAL_REPORT",
}

/**
 * Enum defining options for time entries as a separate report.
 *
 * Superseded by {@link ReportInclusion} / `ReportSettings.timeEntriesInReports`, but still written
 * for IRES Project builds that only read the legacy field; see `resolveTimeEntryInclusions` and
 * `toLegacyTimeEntriesAsSeparateReport`.
 */
export enum TimeEntriesAsSeparateReport {
  NO = "NO",
  YES_TOTAL = "YES_TOTAL",
  YES_INDIVIDUAL = "YES_INDIVIDUAL",
}

/**
 * Where a kind of content (uploaded PDF documents, time entries) appears once a report run is
 * generated. Internal report, external report and a report of the content's own are not mutually
 * exclusive.
 *
 * **Not every value applies to every setting**: PDF documents use `SEPARATE_REPORT`, while time
 * entries choose between the two flavours `SEPARATE_REPORT_TOTAL` and `SEPARATE_REPORT_INDIVIDUAL`,
 * which are exclusive of each other because the entries cannot be both collected into one
 * Einzelbericht and split across several. Use {@link DOCUMENT_INCLUSION_OPTIONS} /
 * {@link TIME_ENTRY_INCLUSION_OPTIONS} rather than `Object.values` when offering the choice, and
 * {@link SEPARATE_TIME_ENTRY_REPORT_OPTIONS} as the picker's exclusive group so the two flavours
 * cannot both be checked.
 *
 * The two flavours keep the distinction the legacy {@link TimeEntriesAsSeparateReport} made ("Ja
 * gesamt" / "Ja einzeln"), which is why they carry the legacy names; see
 * `resolveTimeEntryInclusions` and `toLegacyTimeEntriesAsSeparateReport`.
 *
 * Consumed by IRES Project, which builds the reports. Office only stores the choice.
 */
export enum ReportInclusion {
  /** Part of the internal report. */
  INTERNAL_REPORT = "INTERNAL_REPORT",
  /** Part of the external report. */
  EXTERNAL_REPORT = "EXTERNAL_REPORT",
  /** Produced as a report of its own ("Einzelbericht"). PDF documents only. */
  SEPARATE_REPORT = "SEPARATE_REPORT",
  /** One Einzelbericht holding all time entries ("gesamt"). Legacy `YES_TOTAL`. Time entries only. */
  SEPARATE_REPORT_TOTAL = "SEPARATE_REPORT_TOTAL",
  /** One Einzelbericht per time entry ("einzeln"). Legacy `YES_INDIVIDUAL`. Time entries only. */
  SEPARATE_REPORT_INDIVIDUAL = "SEPARATE_REPORT_INDIVIDUAL",
}

/** The inclusions offered for uploaded PDF documents, in picker order. */
export const DOCUMENT_INCLUSION_OPTIONS = [
  ReportInclusion.INTERNAL_REPORT,
  ReportInclusion.EXTERNAL_REPORT,
  ReportInclusion.SEPARATE_REPORT,
];

/** The inclusions offered for time entries, in picker order. */
export const TIME_ENTRY_INCLUSION_OPTIONS = [
  ReportInclusion.INTERNAL_REPORT,
  ReportInclusion.EXTERNAL_REPORT,
  ReportInclusion.SEPARATE_REPORT_TOTAL,
  ReportInclusion.SEPARATE_REPORT_INDIVIDUAL,
];

/**
 * The two flavours of time-entry Einzelbericht, which exclude each other: the entries cannot be both
 * collected into one report and split across several. Passed to the picker as its exclusive group,
 * so checking one unchecks the other.
 */
export const SEPARATE_TIME_ENTRY_REPORT_OPTIONS = [
  ReportInclusion.SEPARATE_REPORT_TOTAL,
  ReportInclusion.SEPARATE_REPORT_INDIVIDUAL,
];

/**
 * Whether an inclusion asks for content to be produced as a report of its own, in any flavour.
 *
 * @param inclusion The inclusion to check.
 * @returns `true` for all three separate-report values.
 */
export function isSeparateReport(inclusion: ReportInclusion): boolean {
  return inclusion === ReportInclusion.SEPARATE_REPORT
    || inclusion === ReportInclusion.SEPARATE_REPORT_TOTAL
    || inclusion === ReportInclusion.SEPARATE_REPORT_INDIVIDUAL;
}

/**
 * A stored time-entry selection using the values the setting offers today.
 *
 * Between 2026-08-12 and 2026-08-17 the time entries had the same plain `SEPARATE_REPORT` as the
 * documents, so a client saved in that window carries it. It becomes `SEPARATE_REPORT_INDIVIDUAL`,
 * which is what it was written to the legacy field as. Duplicates and a second flavour are dropped
 * with it: the mapping can produce a duplicate, and a document carrying both flavours (only
 * reachable by editing Firestore by hand) would otherwise be shown, and saved again, in a state the
 * screen cannot produce.
 *
 * @param inclusions The stored selection.
 * @returns A copy of the selection with at most one Einzelbericht.
 */
function normalizeStoredTimeEntryInclusions(inclusions: ReportInclusion[]): ReportInclusion[] {
  const normalized = inclusions.map((inclusion) =>
    inclusion === ReportInclusion.SEPARATE_REPORT
      ? ReportInclusion.SEPARATE_REPORT_INDIVIDUAL
      : inclusion
  );

  return normalized.filter((inclusion, index) => {
    const firstOccurrence = normalized.indexOf(inclusion) === index;

    return isSeparateReport(inclusion)
      ? firstOccurrence && normalized.findIndex(isSeparateReport) === index
      : firstOccurrence;
  });
}

/**
 * Enum defining options for measurement protocol as a separate report.
 */
export enum MeasurementProtocolAsSeparateReport {
  NO = "NO",
  YES = "YES",
}

/**
 * Enum defining options for report footer text content.
 */
export enum ReportFooterText {
  APP_VERSION = "APP_VERSION",
  COMPANY = "COMPANY",
  LOCATION = "LOCATION",
}

/**
 * Enum defining options for project info displayed in report footer.
 */
export enum ReportProjectInfo {
  PROJECT_NUMBER = "PROJECT_NUMBER",
  COLLECTION_NUMBER = "COLLECTION_NUMBER",
  PROJECT_NUMBER_AND_COLLECTION_NUMBER = "PROJECT_NUMBER_AND_COLLECTION_NUMBER",
}

/**
 * Enum defining sources for selecting project supervisors.
 */
export enum ProjectSupervisorSource {
  MANUAL_SELECTION = "MANUAL_SELECTION",
  SUPERVISOR_OF_PROJECT_OWNER = "SUPERVISOR_OF_PROJECT_OWNER",
}

/**
 * Interface defining PDF report configuration.
 */
export interface ClientPdfReportConfig {
  imageQuality: ReportImageQuality;
  numImagesPerRow: number;
  showReportConfigInProject: boolean;
  reduceReportSize?: boolean;
  imageReportAfterDamageAssessment?: ImageReportAfterDamageAssessment;
  showDamagePatternInfoInLeakDetectionReport?: boolean;
  /** Order of the sketch, images and text blocks in a report section. Defaults to `SKETCH_IMAGES_TEXT` when unset. */
  contentOrder?: ReportContentOrder;
}

/**
 * Interface defining report email addresses.
 */
export interface ReportEmailAddress {
  damageReportEmailAddress: string | null;
  dryingReportEmailAddress: string | null;
  leakDetectionReportEmailAddress: string | null;
}

/**
 * Interface defining company logo.
 */
export interface CompanyLogo {
  logoFile?: string | null;
  scaleFactor?: number;
}

/**
 * Interface defining report settings.
 */
export interface ReportSettings {
  pdfReportConfig: ClientPdfReportConfig | null;
  reportEmailAddress: ReportEmailAddress;
  saveReport: boolean;
  /**
   * @deprecated
   * `sendReportAsEmail` is deprecated and should not be used. Please see `UserSettings`.
   */
  sendReportAsEmail: boolean;
  
  sendReportsAs?: SendReportsOption;

  reportsList?: ReportsList;
  sendReportAsJson?: boolean;

  companyLogo?: CompanyLogo;
  timeEntriesOnNewPage?: boolean;
  companyAddressInTimeRecord?: boolean;
  workTimeInTimeRecord?: boolean;
  phaseInTimeRecord?: boolean;
  projectDataExchange?: ProjectDataExchangeOptions;
  includeGenericDeviceAssignmentsInReport?: boolean;

  /**
   * Whether a certificate is produced for the device usage. Shown in the GERÄTEEINSATZ section
   * beside the device-assignment switch; Office only stores the switch, the document itself is
   * rendered outside this app.
   */
  createCertificate?: boolean;

  /**
   * Whether contact data appears in the external report. Off by default: the external report is
   * the one that leaves the company, which is why this sits under Datenschutz.
   */
  contactDataInExternalReports?: boolean;

  /**
   * @deprecated
   * `documentsAsSeparateReport` is superseded by `documentsInReports`, but is still written so
   * that IRES Project builds which only read this field keep working.
   */
  documentsAsSeparateReport?: DocumentsAsSeparateReport;
  /**
   * @deprecated
   * `timeEntriesAsSeparateReport` is superseded by `timeEntriesInReports`, but is still written so
   * that IRES Project builds which only read this field keep working.
   */
  timeEntriesAsSeparateReport?: TimeEntriesAsSeparateReport;
  measurementProtocolAsSeparateReport?: MeasurementProtocolAsSeparateReport;

  /** Where uploaded PDF documents appear. Absent for clients that never saved the new setting. */
  documentsInReports?: ReportInclusion[];
  /** Where time entries appear. Absent for clients that never saved the new setting. */
  timeEntriesInReports?: ReportInclusion[];

  reportFooterText?: ReportFooterText;
  reportProjectInfo?: ReportProjectInfo;
  /**
   * Whether the report footer shows the page number together with the total number of pages
   * ("7 von 13") instead of the page number alone ("7"). Defaults to `false` when unset.
   */
  showTotalPageCount?: boolean;

  areDefaultReportSettings?: boolean;
}

/**
 * Where uploaded PDF documents appear, for a client that may never have saved the new setting.
 *
 * `documentsInReports` wins when it is present. Otherwise the choice is derived from the legacy
 * `documentsAsSeparateReport`: a client that had documents as a separate report keeps that report
 * and additionally has them in the internal one; a client that did not had them in both reports.
 *
 * @param settings The client's report settings.
 * @returns The places the documents appear in.
 */
export function resolveDocumentInclusions(
  settings: Pick<ReportSettings, "documentsInReports" | "documentsAsSeparateReport">
): ReportInclusion[] {
  if (settings.documentsInReports) {
    return [...settings.documentsInReports];
  }

  return settings.documentsAsSeparateReport === DocumentsAsSeparateReport.YES
    ? [ReportInclusion.INTERNAL_REPORT, ReportInclusion.SEPARATE_REPORT]
    : [ReportInclusion.INTERNAL_REPORT, ReportInclusion.EXTERNAL_REPORT];
}

/**
 * Where time entries appear, for a client that may never have saved the new setting.
 *
 * `timeEntriesInReports` wins when it is present. Otherwise both legacy "Ja" values become the
 * matching Einzelbericht — `YES_TOTAL` one report holding every entry, `YES_INDIVIDUAL` one report
 * per entry — while `NO` means the entries are part of the internal and the external report.
 *
 * @param settings The client's report settings.
 * @returns The places the time entries appear in.
 */
export function resolveTimeEntryInclusions(
  settings: Pick<ReportSettings, "timeEntriesInReports" | "timeEntriesAsSeparateReport">
): ReportInclusion[] {
  if (settings.timeEntriesInReports) {
    return normalizeStoredTimeEntryInclusions(settings.timeEntriesInReports);
  }

  switch (settings.timeEntriesAsSeparateReport) {
    case TimeEntriesAsSeparateReport.YES_TOTAL:
      return [ReportInclusion.INTERNAL_REPORT, ReportInclusion.SEPARATE_REPORT_TOTAL];
    case TimeEntriesAsSeparateReport.YES_INDIVIDUAL:
      return [ReportInclusion.INTERNAL_REPORT, ReportInclusion.SEPARATE_REPORT_INDIVIDUAL];
    default:
      return [ReportInclusion.INTERNAL_REPORT, ReportInclusion.EXTERNAL_REPORT];
  }
}

/**
 * The legacy `documentsAsSeparateReport` value to write alongside a new selection, so that IRES
 * Project builds which only read the legacy field still produce the separate documents report.
 *
 * @param inclusions The selected places the documents appear in.
 * @returns The matching legacy value.
 */
export function toLegacyDocumentsAsSeparateReport(
  inclusions: ReportInclusion[]
): DocumentsAsSeparateReport {
  return inclusions.some(isSeparateReport)
    ? DocumentsAsSeparateReport.YES
    : DocumentsAsSeparateReport.NO;
}

/**
 * The legacy `timeEntriesAsSeparateReport` value to write alongside a new selection.
 *
 * Both legacy "Ja" values are expressible in the selection, so the mapping is lossless in both
 * directions and nothing has to be preserved from what the client had stored: the selection is the
 * source of truth, and an IRES Project build that reads only the legacy field sees exactly the
 * Einzelbericht that was selected.
 *
 * @param inclusions The selected places the time entries appear in.
 * @returns The matching legacy value.
 */
export function toLegacyTimeEntriesAsSeparateReport(
  inclusions: ReportInclusion[]
): TimeEntriesAsSeparateReport {
  if (inclusions.includes(ReportInclusion.SEPARATE_REPORT_TOTAL)) {
    return TimeEntriesAsSeparateReport.YES_TOTAL;
  }

  return inclusions.includes(ReportInclusion.SEPARATE_REPORT_INDIVIDUAL)
    ? TimeEntriesAsSeparateReport.YES_INDIVIDUAL
    : TimeEntriesAsSeparateReport.NO;
}

/**
 * The current option a stored legacy `tableSorting` value stands for.
 *
 * Declared as a full `Record` on purpose: a new `TableSortingOptions` member would not compile
 * until it is listed here.
 */
const LEGACY_TABLE_SORTING_TO_V2: Record<TableSortingOptions, TableSortingOptionsV2> = {
  [TableSortingOptions.PHASE_TRADE]: TableSortingOptionsV2.PHASE_TRADE,
  [TableSortingOptions.TRADE_PHASE]: TableSortingOptionsV2.TRADE_PHASE,
  [TableSortingOptions.APARTMENT_PHASE]: TableSortingOptionsV2.APARTMENT_PHASE,
  [TableSortingOptions.APARTMENT_TRADE]: TableSortingOptionsV2.APARTMENT_TRADE,
};

/**
 * The legacy value each current option is written as, for IRES Project builds that only know the
 * four original options. The room-level choices map to their apartment-level equivalent: it is the
 * finest grouping a legacy build has, and it keeps the phase-first or trade-first order the user
 * chose instead of dropping back to the default.
 *
 * Declared as a full `Record` on purpose, and this is the guard that matters: an option added to
 * `TableSortingOptionsV2` does not compile until someone decides what legacy value it degrades to.
 * Extending the enum without that decision is exactly what broke IRES Project.
 */
const V2_TABLE_SORTING_TO_LEGACY: Record<TableSortingOptionsV2, TableSortingOptions> = {
  [TableSortingOptionsV2.PHASE_TRADE]: TableSortingOptions.PHASE_TRADE,
  [TableSortingOptionsV2.TRADE_PHASE]: TableSortingOptions.TRADE_PHASE,
  [TableSortingOptionsV2.APARTMENT_PHASE]: TableSortingOptions.APARTMENT_PHASE,
  [TableSortingOptionsV2.APARTMENT_TRADE]: TableSortingOptions.APARTMENT_TRADE,
  [TableSortingOptionsV2.ROOM_PHASE]: TableSortingOptions.APARTMENT_PHASE,
  [TableSortingOptionsV2.ROOM_TRADE]: TableSortingOptions.APARTMENT_TRADE,
};

/**
 * How the trades table is sorted, for a client that may never have saved the new setting.
 *
 * `tableSortingV2` wins when it holds an option this build knows. Otherwise the deprecated
 * `tableSorting` is widened to its current equivalent, so a client that last saved before the
 * versioned field existed keeps the sorting they configured.
 *
 * Both stored values are validated rather than trusted: they come from a document any build may
 * have written, and an unrecognised string has to degrade to the default here instead of reaching
 * the picker, which would render no selection at all.
 *
 * @param settings The client's project manager settings.
 * @returns The option to display and edit.
 */
export function resolveTableSorting(
  settings: Pick<ProjectManagerSettings, "tableSorting" | "tableSortingV2">
): TableSortingOptionsV2 {
  const stored = settings.tableSortingV2;
  if (stored !== undefined && Object.values(TableSortingOptionsV2).includes(stored)) {
    return stored;
  }

  // Membership in the enum, not `in` on the lookup: `in` would also answer true for an inherited
  // Object property, so a stored "toString" would resolve to a function.
  const legacy = settings.tableSorting;
  if (legacy !== undefined && Object.values(TableSortingOptions).includes(legacy)) {
    return LEGACY_TABLE_SORTING_TO_V2[legacy];
  }

  return TableSortingOptionsV2.PHASE_TRADE;
}

/**
 * The legacy `tableSorting` value to write alongside a new selection, so that IRES Project builds
 * which only read the legacy field keep deserializing their settings.
 *
 * @param value The selected sorting option.
 * @returns The matching legacy value.
 */
export function toLegacyTableSorting(value: TableSortingOptionsV2): TableSortingOptions {
  return V2_TABLE_SORTING_TO_LEGACY[value];
}

/**
 * Interface defining user settings.
 */
export interface UserSettings {
  cockpitActiveForAllUsers: boolean;
  keepProjectOwner?: boolean;
  projectSupervisorSource?: ProjectSupervisorSource;
  strictBranchFilters?: boolean;
  minProgramChainDurationInMin?: number;
  minGeneralGapDurationInMin?: number;
  /**
   * How close to its destination a drive counts as arrived, in metres (FAHRT, IRES Work).
   *
   * Straight-line distance. Beyond it the travel planner offers UNTERBRECHEN instead of ANGEKOMMEN, so
   * a crew that stops short cannot record a drive as completed by reflex. Hans's figure is 200 m, which
   * is the default when this is absent; a client-wide policy rather than a per-user preference, so that
   * "arrived" means one thing in one client's data. Written here and read by IRES Work; Office itself
   * does not use it.
   */
  travelArrivalRadiusInMeters?: number;

  areDefaultUserSettings?: boolean;
}

/**
 * Interface defining company settings.
 */
export interface CompanySettings {
  language: Language;
  usePrimaryLanguageForReports: boolean;
  preferAbsHumidity?: boolean;

  /** IANA timezone identifier (e.g. "Europe/Berlin"). Used to display all dates/times in the client's local time. */
  timezone?: string;

  /**
   * How an address is written out, as an ordered list of its parts (Hans, 2026-08-17).
   *
   * A client-wide **display** preference, which is why it sits beside language, humidity unit and
   * timezone rather than with the project settings: the same rule applies to a project's damage
   * location, a branch, a user's home address and a drive's destination. Country specific by nature
   * ("street first, without PLZ"), so a client working in one country writes it once.
   *
   * Absent means [DEFAULT_ADDRESS_FORMAT_TEMPLATE], which is what every IRES product renders today.
   * Deliberately **not** used for the geocoder's query string: that has a fixed shape (street and
   * number, PLZ and city, country code last) because without the country an identically named street
   * abroad wins the lookup.
   */
  addressFormatTemplate?: AddressTemplateField[];

  /**
   * Whether this client works with addresses on the map (Hans, ticket 2609016).
   *
   * Sits directly after [addressFormatTemplate] because it is the other client-wide decision about
   * addresses, and it is the switch that brings geocoding into the **foreground**: the pin beside
   * every address, the picker it opens, and FAHRT2 in IRES Work.
   *
   * **Geocoding itself does not depend on it.** IRES Cloud positions addresses on write regardless,
   * so a client turning this on finds its existing addresses already placed rather than starting
   * from nothing. What this controls is whether anyone but IRES can see them.
   *
   * Absent means **off**: the feature appears only when a client asks for it. Only offered when
   * KARTE is on in the system configuration, and an IRES admin sees the map features whatever it
   * says.
   */
  enableAddressGeocoding?: boolean;

  areDefaultCompanySettings?: boolean;
}

/**
 * The parts an address can be built from, for [CompanySettings.addressFormatTemplate].
 *
 * Separate from [ProjectNameTemplateField] on purpose, even though four members overlap: a project
 * name may carry a project number and a contact's name, which an address never does, and an address
 * may carry a country, which a project name never does. One shared enum would offer each screen
 * fields it cannot render.
 */
export enum AddressTemplateField {
  STREET = "STREET",
  HOUSE_NUMBER = "HOUSE_NUMBER",
  ZIP_CODE = "ZIP_CODE",
  CITY = "CITY",
  COUNTRY = "COUNTRY",
  /** Literal `,` separator. Repeatable, and never first or last. */
  COMMA = "COMMA",
}

/** Default address format: Straße Hausnummer, PLZ Ort. What the products render with no setting. */
export const DEFAULT_ADDRESS_FORMAT_TEMPLATE: AddressTemplateField[] = [
  AddressTemplateField.STREET,
  AddressTemplateField.HOUSE_NUMBER,
  AddressTemplateField.COMMA,
  AddressTemplateField.ZIP_CODE,
  AddressTemplateField.CITY,
];

/**
 * Settings for a HyDry device.
 */
export interface HyDryDeviceSettings {
  comBoxClientConfigInfo?: ComBoxClientConfigInfo;
  miniClientConfigInfo?: ComBoxClientConfigInfo;
  minRunDurationForAnalysis?: number;
  alwaysShowSerialNumber?: boolean;

  areDefaultHyDryDeviceSettings?: boolean;
}

/**
 * Enumeration for different options regarding project settings.
 */
export enum ProjectSettingsOptions {
  PROJECT_MANAGER = "PROJECT_MANAGER",
  DEVICE_MANAGER = "DEVICE_MANAGER",
  PORTAL = "PORTAL",
  API = "API",
  BOTH = "BOTH", // Deprecated
  NEVER = "NEVER",  // Deprecated
}

/**
 * Enumeration for different standard identification options.
 */
export enum StandardIdentificationOptions {
  QR_CODE = "QR_CODE",
  BARCODE = "BARCODE",
  NFC = "NFC",
}

export interface ProjectSettingsDeprecated {
  editProjectsIn: ProjectSettingsOptions;
  finaliseProjectsIn: ProjectSettingsOptions;
  newAppointmentsIn: ProjectSettingsOptions;
  newProjectsIn: ProjectSettingsOptions;

  areDefaultProjectSettings?: boolean;
}

/**
 * Represents project settings.
 */
export interface ProjectSettingsV2 {
  editProjectsIn: ProjectSettingsOptions[];
  finaliseProjectsIn: ProjectSettingsOptions[];
  newAppointmentsIn: ProjectSettingsOptions[];
  newProjectsIn: ProjectSettingsOptions[];

  areDefaultProjectSettings?: boolean;
}

/**
 * Represents settings for project editing.
 */
export interface ProjectEditingSettings {
  defaultDryingDurationInDays: number;
  defaultFollowUpInspectionIntervalInDays: number;

  areDefaultProjectEditingSettings?: boolean;
}

/**
 * Display control settings for project views.
 */
export interface DisplaySettings {
  projectOverviewTilesForOfficeProjects: boolean;
}

/**
 * Represents settings for project devices.
 */
export interface ProjectDevicesSettings {
  enforceMeterReading: boolean;
  standardIdentification: StandardIdentificationOptions;

  areDefaultProjectDevicesSettings?: boolean;
}

/**
 * Represents import configuration settings.
 */
export interface ImportConfiguration {
  newCategoryAllowed: boolean;
  newTemplateAllowed: boolean;
  updateAllowed: boolean;

  defaultImportConfiguration?: boolean;
}

/**
 * Enumeration for different options regarding action buttons.
 */
export enum ActionButtonOptions {
  RED = "RED",
  YELLOW = "YELLOW",
  HIDE = "HIDE",
}

/**
 * Determines which project phase measurement points are pre-initialized in.
 */
export enum MeasurementPointPresetPhase {
  LEAK_DETECTION = "LEAK_DETECTION",
  DRYING = "DRYING",
}

/**
 * Represents settings for project manager app.
 */
export interface ProjectManagerSettings {
  projectCause: ActionButtonOptions;
  projectAttendees: ActionButtonOptions;
  pipeSystem?: ActionButtonOptions;
  leakDetectionSketchAndMeasurementPoints?: ActionButtonOptions;
  apartmentAttendees?: ActionButtonOptions;
  tradeRenovation?: ActionButtonOptions;

  canAddMeasurementPointsInApartment: boolean;
  roomClimateMeasuringPoints: ActionButtonOptions;
  measurementPointPresetPhase?: MeasurementPointPresetPhase;

  /**
   * @deprecated preferAbsHumidity is deprecated and should not be used.
   */
  preferAbsHumidity?: boolean;

  enforceFullDamageAssessment: boolean;
  dryingSetupEnquiry?: boolean;

  sketchInRooms: ActionButtonOptions;
  presetRoomHeight: number;

  /**
   * Preset thickness in cm of a new INNER_WALL in the IRES Project sketch. 0 means no preset.
   * Office only edits these three values, IRES Project applies them.
   */
  presetInnerWallThickness?: number;

  /** Preset thickness in cm of a new OUTER_WALL in the IRES Project sketch. 0 means no preset. */
  presetOuterWallThickness?: number;

  /** Preset thickness in cm of a new PARTY_WALL in the IRES Project sketch. 0 means no preset. */
  presetPartyWallThickness?: number;

  hintPhotoQuality: boolean;

  /**
   * @deprecated
   * `tableSorting` is superseded by `tableSortingV2`, but is still written so that IRES Project
   * builds which only read this field keep working. It only ever holds one of the four legacy
   * options: a room-level choice is written here as its apartment-level equivalent. Use
   * `resolveTableSorting` to read and `toLegacyTableSorting` to write it.
   */
  tableSorting?: TableSortingOptions;
  /** How the trades table is grouped and sorted. Absent for clients that never saved the setting. */
  tableSortingV2?: TableSortingOptionsV2;
  tableAutomaticallyInReport?: boolean;
  /**
   * Whether the Aufmaß (the quantity survey of the trades) goes into the report. Office only
   * stores it; IRES Project applies it. Absent for clients that never saved the setting, which
   * reads as `false` (Nein), the initial value Hans asked for (#2609052).
   */
  quantitySurveyInReport?: boolean;

  presetFlexibleServiceList?: boolean;

  areDefaultProjectManagerSettings?: boolean;
}

/**
 * Represents settings for device manager app.
 */
export interface DeviceManagerSettings {
  canUserAddProjects: boolean;
  canUserAddContactPersons: boolean;
  canUserAddRooms: boolean;
  canUserAddDevices: boolean;
  recentlyCompletedTask?: number;

  areDefaultDeviceManagerSettings?: boolean;
}

/**
 * Fields available for the automatic project name template.
 * Clients can configure the order and selection of these fields
 * to control how auto-generated project names are composed.
 */
export enum ProjectNameTemplateField {
  PROJECT_NUMBER = "PROJECT_NUMBER",
  ZIP_CODE = "ZIP_CODE",
  CITY = "CITY",
  STREET = "STREET",
  HOUSE_NUMBER = "HOUSE_NUMBER",
  NAME = "NAME",
  COMMA = "COMMA",
}

/** Default template: Straße Hausnummer, PLZ Ort */
export const DEFAULT_PROJECT_NAME_TEMPLATE: ProjectNameTemplateField[] = [
  ProjectNameTemplateField.STREET,
  ProjectNameTemplateField.HOUSE_NUMBER,
  ProjectNameTemplateField.COMMA,
  ProjectNameTemplateField.ZIP_CODE,
  ProjectNameTemplateField.CITY,
];

/**
 * Device assignment settings, edited under EINSTELLUNGEN PROJEKTBEARBEITUNG -> Gerätezuordnung.
 *
 * Deliberately not part of `ProjectManagerSettings`: that object configures the IRES Project app,
 * and device assignment is a project-wide rule that IRES Office and IRES Work are expected to read
 * as well. Moved out of it on 2026-09-17 (Hans), with IRES Project adapted in the same round.
 */
export interface DeviceAssignmentSettings {
  /**
   * When true, a device is assigned to a single room rather than to the apartment as a whole.
   * A device assigned to a room still belongs to its apartment, so nothing that displays an
   * assignment has to change. Unset means false.
   */
  roomLevelDeviceAssignment?: boolean;
}

/**
 * Represents settings for project management.
 */
export interface ProjectManagementSettings {
  projectEditingSettings?: ProjectEditingSettings;
  projectSettings?: ProjectSettingsDeprecated;
  projectSettingsV2?: ProjectSettingsV2;
  projectManagerSettings?: ProjectManagerSettings;
  deviceManagerSettings?: DeviceManagerSettings;
  displaySettings?: DisplaySettings;
  deviceAssignmentSettings?: DeviceAssignmentSettings;
  projectTypes?: ProjectTypeConfig[];
  /** Configurable field order for automatic project name generation. */
  projectNameTemplate?: ProjectNameTemplateField[];

  areDefaultProjectManagementSettings?: boolean;
}

/**
 * Represents settings for all devices.
 */
export interface AllDevicesSettings {
  projectDevicesSettings?: ProjectDevicesSettings;
  importConfiguration?: ImportConfiguration;

  areDefaultAllDevicesSettings?: boolean;
}

/**
 * Represents settings for finished projects.
 */
export interface FinishedProjectsSettings {
  currentProjectsNotTouched: NumberOfMonths;
  deleteProjectsNotProcessed: NumberOfWeeks;
  moveFinishedProjects: NumberOfMonths;
  moveProjectsClosed: NumberOfWeeks;
  moveProjectsDamagePatternClosed: NumberOfWeeks;
  moveProjectsOnHold: NumberOfMonths;
  recentlyFinishedProjects: NumberOfDays;

  areDefaultFinishedProjectsSettings?: boolean;
}

export function getDefaultNumberOfMonthsFromNumberOfWeeks(numberOfMonths: NumberOfMonths) {
  if (!Object.values(NumberOfMonths).includes(numberOfMonths)) {
    return NumberOfMonths.THREE;
  }

  return numberOfMonths;
}

/**
 * The Projektorganisation choices the client can make (where projects are created, edited and finished,
 * and where appointments are created). IRES Work is offered when the client has it (#2610083): by the
 * client, not by who is looking, so a client admin sees and keeps the same choices as IRES.
 */
export function getProjectSettingsOptions() {
  const clientInfo = getClientInfo();
  const projectManagement = getProjectManagement();

  const hasApi =
    clientInfo && 
    (
      clientInfo.erpIntegration !== ErpIntegration.NONE ||
      clientInfo.syncsProjectWithErp
    );
  const hasProjectAppAccess =
    clientInfo?.hasApp &&
    (projectManagement === ProjectManagement.DEFAULT ||
      projectManagement === ProjectManagement.PROJECT_OPTION);
  const hasWorkAppAccess = clientInfo?.hasWorkAccess === true;

  return Object.values(ProjectSettingsOptions).filter((option) => {
    switch (option) {
      case ProjectSettingsOptions.API:
        return hasApi;
      case ProjectSettingsOptions.DEVICE_MANAGER:
        return hasWorkAppAccess;
      case ProjectSettingsOptions.PROJECT_MANAGER:
        return hasProjectAppAccess;
      case ProjectSettingsOptions.BOTH:
      case ProjectSettingsOptions.NEVER:
        return false;

      default:
        return true;
    }
  });
}

/**
 * A stored Projektorganisation choice as IRES Office applies it: the deprecated single values expanded,
 * and choices the client cannot make dropped. IRES Work is always kept (#2610083): IRES Office reads no
 * IRES Work choice, and dropping it here used to remove it from the client's settings on the next save.
 */
function calculateProjectSettingsOptions(options: ProjectSettingsOptions | ProjectSettingsOptions[]) {
  const projectSettingsOptions = getProjectSettingsOptions();

  if (Array.isArray(options)) {
    return options.filter(
      (option) => option === ProjectSettingsOptions.DEVICE_MANAGER || projectSettingsOptions.includes(option)
    );
  }
  else {
    if (options === ProjectSettingsOptions.BOTH) {
      return projectSettingsOptions;
    }
    else if (options === ProjectSettingsOptions.NEVER) {
      return [];
    }
    else {
      return options === ProjectSettingsOptions.DEVICE_MANAGER || projectSettingsOptions.includes(options)
        ? [options]
        : [];
    }
  }
}

/**
 * The settings document without its Projektorganisation (`projectSettingsV2` and the deprecated
 * `projectSettings`), for every save from IRES Office's settings pages. Since 2026-10-29 only the IRES Admin
 * Client sets it (through IRES Cloud), so no page writes it back: with `merge: true` the stored answers
 * stay as they are, and a page's stale copy cannot undo a change made there.
 *
 * @param settings - the document a page is about to save.
 * @returns a copy without the two fields; the input is not changed.
 */
export function withoutProjectOrganisation(settings: ProjectManagementSettings): ProjectManagementSettings {
  const copy = { ...settings };
  delete copy.projectSettingsV2;
  delete copy.projectSettings;
  return copy;
}

export function getProjectSettingsV2(
  projectManagementSettings: ProjectManagementSettings
): ProjectSettingsV2 {
  const projectSettingsDeprecated = projectManagementSettings.projectSettings;
  const projectSettingsV2 = projectManagementSettings.projectSettingsV2;

  const projectSettings = projectSettingsV2 && !projectSettingsV2.areDefaultProjectSettings
    ? projectSettingsV2
    : projectSettingsDeprecated;

  return projectSettings
  ? {
      editProjectsIn: calculateProjectSettingsOptions(projectSettings.editProjectsIn),
      finaliseProjectsIn: calculateProjectSettingsOptions(projectSettings.finaliseProjectsIn),
      newAppointmentsIn: calculateProjectSettingsOptions(projectSettings.newAppointmentsIn),
      newProjectsIn: calculateProjectSettingsOptions(projectSettings.newProjectsIn),

      areDefaultProjectSettings: projectSettings.areDefaultProjectSettings
    }
  : createDefaultProjectSettingsV2();
}